/**
 * ============================================================================
 * EventPassStart.js — /events/start, the Event Pass door (Sept 30, 2026)
 * ============================================================================
 * One screen for a stranger who came to buy ONE event: pick the pass, name the
 * event, give his name / email / password, and go straight to payment. No plan
 * picker, no "Free account · up to 8 players" welcome. Behind the scenes:
 *
 *   1. POST /api/auth/register/free  { eventAccount: true, organizationName: <event> }
 *      → a free account named after the event, marked event_account, no Free
 *        welcome email; tokens are stored so he is signed in.
 *   2. POST /api/event-pass/checkout { kind, return_path: '/events/ready' }
 *      → Stripe Checkout. Success and cancel both come back to /events/ready
 *        (EventPassReady.js), which confirms the pass and shows Next Steps, or
 *        offers the payment again.
 *
 * ?size=72|250 preselects the pass (the /events card sends 72 by default).
 * Signed in already? The pass goes on that account: we open the dashboard's Event
 * Pass window (the tto_open_event_pass flag App.js reads once), since a signed-in
 * organizer's price depends on his plan.
 *
 * Visual style follows RegisterPage: green gradient, centred white card.
 * ============================================================================
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOutsideUSNotice } from './OutsideUSNotice';   // Oct 10, 2026

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

const PASSES = {
  standard_72: { size: 72, price: 135, label: 'Up to 72 golfers', sub: 'Most outings and member-guests' },
  standard_250: { size: 250, price: 185, label: 'Up to 250 golfers', sub: 'Full-field shotguns and big days' },
};

const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') window.gtag('event', eventName, params);
};

const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'event';

const green = '#2d6a4f';
const label = { display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 };
const input = { width: '100%', boxSizing: 'border-box', padding: '11px 12px', fontSize: 16, border: '1px solid #d1d5db', borderRadius: 8, outline: 'none' };
const hint = { fontSize: 12.5, color: '#6b7280', marginTop: 5, lineHeight: 1.45 };

export function StepDots({ at }) {
  const steps = ['Your event', 'Payment', 'Next steps'];
  return (
    <ol aria-label="Progress" style={{ display: 'flex', gap: 6, listStyle: 'none', padding: 0, margin: '0 0 18px', justifyContent: 'center', flexWrap: 'wrap' }}>
      {steps.map((s, i) => {
        const n = i + 1;
        const state = n < at ? 'done' : n === at ? 'now' : 'next';
        return (
          <li key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: state === 'next' ? '#9ca3af' : green }}>
            <span style={{
              width: 22, height: 22, borderRadius: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
              background: state === 'next' ? '#f3f4f6' : state === 'done' ? '#dcfce7' : green,
              color: state === 'now' ? '#fff' : state === 'done' ? green : '#9ca3af',
            }}>{state === 'done' ? '✓' : n}</span>
            {s}
            {n < steps.length && <span aria-hidden="true" style={{ color: '#d1d5db', margin: '0 2px' }}>›</span>}
          </li>
        );
      })}
    </ol>
  );
}

export function PassChoice({ kind, onChange }) {
  return (
    <div role="radiogroup" aria-label="Choose your Event Pass" style={{ display: 'grid', gap: 10 }}>
      {Object.entries(PASSES).map(([k, p]) => {
        const on = kind === k;
        return (
          <button key={k} type="button" role="radio" aria-checked={on} data-testid={`pass-${k}`} onClick={() => onChange(k)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, textAlign: 'left', width: '100%',
              padding: '13px 15px', borderRadius: 12, cursor: 'pointer', background: on ? '#f0fdf4' : '#fff',
              border: on ? `2px solid ${green}` : '1px solid #d1d5db', margin: 0,
            }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 9, border: `2px solid ${on ? green : '#9ca3af'}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {on && <span style={{ width: 8, height: 8, borderRadius: 4, background: green }} />}
              </span>
              <span>
                <span style={{ display: 'block', fontWeight: 800, fontSize: 16, color: '#111827' }}>{p.label}</span>
                <span style={{ display: 'block', fontSize: 13, color: '#6b7280' }}>{p.sub}</span>
              </span>
            </span>
            <span style={{ fontWeight: 800, fontSize: 20, color: '#111827' }}>${p.price}</span>
          </button>
        );
      })}
    </div>
  );
}

export const passKinds = PASSES;

export default function EventPassStart() {
  const navigate = useNavigate();
  const outsideNotice = useOutsideUSNotice(API_URL);   // Oct 10, 2026: outside-the-US sign-up notice
  const [params] = useSearchParams();
  const auth = useAuth() || {};
  const initialKind = params.get('size') === '250' ? 'standard_250' : 'standard_72';
  const [kind, setKind] = useState(initialKind);
  const [form, setForm] = useState({ eventName: '', name: '', email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [emailTaken, setEmailTaken] = useState(false);
  const pass = PASSES[kind];

  const problems = useMemo(() => {
    const p = {};
    if (!form.eventName.trim()) p.eventName = 'Name your event.';
    if (!form.name.trim()) p.name = 'Your name, please.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) p.email = 'An email address we can reach you at.';
    if (form.password.length < 8) p.password = 'At least 8 characters.';
    if (!agree) p.agree = 'Please agree to continue.';
    return p;
  }, [form, agree]);
  const [touched, setTouched] = useState(false);
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); if (k === 'email') setEmailTaken(false); };

  const loginToBuy = () => {
    try { localStorage.setItem('tto_open_event_pass', '1'); } catch (e) { /* storage blocked */ }
    navigate('/dashboard');
  };

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(problems).length || busy) return;
    outsideNotice.guard(() => createAccount());   // Oct 10, 2026: notice first when outside the US and Canada
  };

  const createAccount = async () => {
    setBusy(true); setError(''); setEmailTaken(false);
    trackEvent('event_pass_start_submit', { kind });
    try { localStorage.setItem('tto_event_pass_kind', kind); } catch (err) { /* ignore */ }

    let attribution = {};
    try { attribution = JSON.parse(sessionStorage.getItem('tto_attribution') || '{}') || {}; } catch (err) { /* ignore */ }

    // 1. The account, named after the event.
    let reg;
    try {
      const r = await fetch(`${API_URL}/auth/register/free`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password,
          name: form.name.trim(),
          organizationName: form.eventName.trim(),
          // A random tail keeps "Member-Guest" from colliding with last year's or another club's.
          organizationSlug: `${slugify(form.eventName)}-${Math.random().toString(36).slice(2, 7)}`,
          eventAccount: true,
          subscriptionTier: 'free',
          utmSource: attribution.utm_source || null,
          utmMedium: attribution.utm_medium || null,
          utmCampaign: attribution.utm_campaign || null,
          utmContent: attribution.utm_content || null,
          landingPage: attribution.landing_page || '/events/start',
          outsideNoticeAccepted: outsideNotice.accepted(),
        }),
      });
      reg = await r.json().catch(() => ({}));
      if (r.status === 409 && /email/i.test(reg.error || '')) { setEmailTaken(true); setBusy(false); return; }
      if (!r.ok) throw new Error(reg.error || 'We couldn’t create your account. Please try again.');
    } catch (err) {
      setError(err.message); setBusy(false); return;
    }
    try {
      if (reg.accessToken) localStorage.setItem('accessToken', reg.accessToken);
      if (reg.refreshToken) localStorage.setItem('refreshToken', reg.refreshToken);
      if (reg.user) localStorage.setItem('user', JSON.stringify(reg.user));
      if (reg.organization) {
        localStorage.setItem('organization', JSON.stringify({ ...reg.organization, role: 'owner' }));
        localStorage.setItem('organizationId', reg.organization.id);
      }
    } catch (err) { /* storage blocked: the Ready page will ask him to log in */ }
    trackEvent('free_signup', { has_referral: false, door: 'event_pass' });

    // 2. Straight to payment.
    try {
      const r = await fetch(`${API_URL}/event-pass/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${reg.accessToken}` },
        credentials: 'include',
        body: JSON.stringify({ kind, return_path: '/events/ready' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.url) throw new Error(d.error || 'checkout');
      trackEvent('event_pass_checkout_open', { kind, door: 'start' });
      window.location.assign(d.url);
    } catch (err) {
      // The account exists; the Ready page offers the payment again.
      navigate('/events/ready?event_pass=cancelled&reason=checkout', { replace: true });
    }
  };

  const shell = (children) => (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: 'linear-gradient(135deg, #2d6a4f, #1b4332)', padding: '24px 16px', boxSizing: 'border-box' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '26px 24px', width: '100%', maxWidth: 500, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', boxSizing: 'border-box' }}>
        {children}
      </div>
    </div>
  );

  if (auth.isAuthenticated) {
    const orgName = (auth.organization && auth.organization.name) || 'your account';
    return shell(
      <div style={{ display: 'grid', gap: 14 }} data-testid="start-signed-in">
        <div style={{ fontSize: 30, textAlign: 'center' }}>🎟️</div>
        <h1 style={{ fontSize: 21, fontWeight: 800, color: '#1f2937', margin: 0, textAlign: 'center' }}>Buy an Event Pass</h1>
        <p style={{ fontSize: 15, color: '#374151', margin: 0, lineHeight: 1.55, textAlign: 'center' }}>
          You&rsquo;re signed in to <strong>{orgName}</strong>. The pass goes on that account, at your plan&rsquo;s price.
        </p>
        <button type="button" onClick={loginToBuy} style={{ padding: '13px 16px', borderRadius: 10, border: 'none', background: green, color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
          Continue to my dashboard
        </button>
        <p style={{ ...hint, textAlign: 'center', margin: 0 }}>
          Running a separate event with its own account?{' '}
          <button type="button" onClick={async () => { try { await auth.logout(); } catch (e) { /* ignore */ } window.location.reload(); }}
            style={{ background: 'none', border: 'none', padding: 0, color: green, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}>
            Log out and start a new one
          </button>.
        </p>
      </div>
    );
  }

  const err = (k) => touched && problems[k] ? <div style={{ color: '#b91c1c', fontSize: 12.5, marginTop: 5 }}>{problems[k]}</div> : null;

  return shell(
    <form onSubmit={submit} noValidate>
      {outsideNotice.modal}
      <StepDots at={1} />
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1f2937', margin: '0 0 4px', textAlign: 'center' }}>Get your Event Pass</h1>
      <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 18px', textAlign: 'center', lineHeight: 1.5 }}>
        One payment for one event. No subscription.
      </p>

      <div style={{ marginBottom: 18 }}>
        <span style={label}>How big is your field?</span>
        <PassChoice kind={kind} onChange={setKind} />
        <div style={hint}>Not sure? Start with 72. You can upgrade to 250 later for the $50 difference.</div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={label} htmlFor="ep-event">Event name</label>
        <input id="ep-event" style={input} value={form.eventName} onChange={set('eventName')} placeholder="e.g. Last Chance Invitational 2026" autoComplete="off" />
        <div style={hint}>Your account is named after your event. It goes on the leaderboard and scorecards, and you can change it later.</div>
        {err('eventName')}
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={label} htmlFor="ep-name">Your name</label>
        <input id="ep-name" style={input} value={form.name} onChange={set('name')} autoComplete="name" />
        {err('name')}
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={label} htmlFor="ep-email">Email</label>
        <input id="ep-email" type="email" style={input} value={form.email} onChange={set('email')} autoComplete="email" />
        {err('email')}
        {emailTaken && (
          <div data-testid="email-taken" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', marginTop: 8, fontSize: 13.5, color: '#78350f', lineHeight: 1.5 }}>
            There&rsquo;s already a Tee Time Organizer account for this email.{' '}
            <button type="button" onClick={loginToBuy} style={{ background: 'none', border: 'none', padding: 0, color: green, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}>
              Log in to buy your pass
            </button>{' '}or use a different email.
          </div>
        )}
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={label} htmlFor="ep-pw">Choose a password</label>
        <div style={{ position: 'relative' }}>
          <input id="ep-pw" type={showPw ? 'text' : 'password'} style={{ ...input, paddingRight: 64 }} value={form.password} onChange={set('password')} autoComplete="new-password" />
          <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 8, top: 8, background: 'none', border: 'none', color: green, fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: '4px 6px' }}>
            {showPw ? 'Hide' : 'Show'}
          </button>
        </div>
        <div style={hint}>You&rsquo;ll use it to log in and run your event.</div>
        {err('password')}
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', margin: '4px 0 16px' }}>
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
        <span style={{ fontSize: 13.5, color: '#374151' }}>
          I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: green }}>Terms of Service</a> and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: green }}>Privacy Policy</a>
        </span>
      </label>
      {err('agree')}

      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontSize: 13.5, color: '#374151', lineHeight: 1.55, margin: '0 0 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 15, color: '#111827' }}>
          <span>Event Pass, {pass.label.toLowerCase()}</span><span>${pass.price}</span>
        </div>
        Good for one event within 6 months. If it rains out, the pass moves to your new date. Your golfers join free as guests.
      </div>

      {error && <div style={{ color: '#b91c1c', fontSize: 14, margin: '0 0 12px' }}>{error}</div>}

      <button type="submit" disabled={busy} data-testid="continue-to-payment"
        style={{ width: '100%', padding: '14px 16px', borderRadius: 10, border: 'none', background: busy ? '#9ca3af' : green, color: '#fff', fontSize: 17, fontWeight: 800, cursor: busy ? 'default' : 'pointer' }}>
        {busy ? 'Opening secure checkout…' : `Continue to payment — $${pass.price}`}
      </button>
      <p style={{ ...hint, textAlign: 'center', margin: '10px 0 0' }}>
        Card payment on the next screen, by Stripe. Then we&rsquo;ll walk you through setting up your event.
      </p>
      <p style={{ ...hint, textAlign: 'center', margin: '12px 0 0' }}>
        Already have an account?{' '}
        <button type="button" onClick={loginToBuy} style={{ background: 'none', border: 'none', padding: 0, color: green, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}>Log in to buy</button>
        {' '}· <a href="/events" style={{ color: green }}>Back to events</a>
      </p>
    </form>
  );
}
